import { makeStyles } from '@material-ui/styles'
import { volumesOverviewDoc } from 'app/docLinks'
import LinkButton from 'core/components/buttons/LinkButton'
import DocumentMeta from 'core/components/DocumentMeta'
import useListAction from 'core/hooks/useListAction'
import Theme from 'core/themes/model'
import { getBlueprints } from 'openstack/components/infrastructure/actions'
import { Blueprint } from 'openstack/components/infrastructure/blueprint/model'
import { listVolumeTypes } from 'openstack/components/storage/volume-types/actions'
import { volumeTypesSelector } from 'openstack/components/storage/volume-types/selectors'
import { listNonPaginatedVolumes } from 'openstack/components/storage/volumes/actions'
import { nonPaginatedVolumesSelector } from 'openstack/components/storage/volumes/selectors'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import StorageDashboardHeader from './StorageDashboardHeader'
import VolumeBackends from './VolumeBackends'

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'grid',
    gap: 16,
  },
}))

export default function StorageDashboardPage() {
  const classes = useStyles()
  const [currentBlueprint, setCurrentBlueprint] = useState<Blueprint>({} as Blueprint)

  useListAction(listNonPaginatedVolumes)
  const volumes = useSelector(nonPaginatedVolumesSelector)

  const { reload: reloadVolumeTypes } = useListAction(listVolumeTypes)
  const volumeTypes = useSelector(volumeTypesSelector)

  // Load blueprint and volume types when component mounts
  useEffect(() => {
    const makeCalls = async () => {
      reloadVolumeTypes(true)
      const blueprints = await getBlueprints()
      setCurrentBlueprint(blueprints?.[0])
    }
    makeCalls()
  }, [])

  const volumeTypesByBackend = useMemo(() => {
    // Must get volume backend from volume type
    const volumeBackendsByVolumeType = volumeTypes?.reduce((accum, type) => {
      return {
        ...accum,
        [type?.name]: type?.extra_specs?.volume_backend_name,
      }
    }, {})

    return volumes.reduce((accum, volume) => {
      const volumeBackend = volumeBackendsByVolumeType[volume.volume_type]
      if (!volumeBackend || !volume.volume_type) {
        return accum
      }
      if (accum[volumeBackend]) {
        if (accum[volumeBackend]?.[volume.volume_type]) {
          return {
            ...accum,
            [volumeBackend]: {
              ...accum[volumeBackend],
              [volume.volume_type]: [...accum[volumeBackend][volume.volume_type], volume],
            },
          }
        } else {
          return {
            ...accum,
            [volumeBackend]: {
              ...accum[volumeBackend],
              [volume.volume_type]: [volume],
            },
          }
        }
      } else {
        return {
          ...accum,
          [volumeBackend]: {
            [volume.volume_type]: [volume],
          },
        }
      }
    }, {})
  }, [volumes, volumeTypes])

  return (
    <>
      <DocumentMeta title="Storage Dashboard" breadcrumbs />
      <div className={classes.container}>
        <div>
          <LinkButton url={volumesOverviewDoc}>Storage Documentation</LinkButton>
        </div>
        <StorageDashboardHeader blueprint={currentBlueprint} volumes={volumes} />
        <VolumeBackends
          blueprint={currentBlueprint}
          volumeTypesByBackend={volumeTypesByBackend}
          volumeTypes={volumeTypes}
        />
      </div>
    </>
  )
}
